import React, { useState, SyntheticEvent } from 'react';
import { Modal, Button, Descriptions, Tag, Input, Tooltip } from 'antd';
import { deleteInference } from '../../../lib/api/aws-api';
import { createTraining } from '../../../lib/api/training-api';
import { getLabelValue } from '../../../lib/util';
import UploadContainer from '../upload/UploadContainer';
import { Model, ModelVersion, InferenceFile, Training } from '../../../types';
import { BlockedDiv } from '../../../components/BlockedDiv';
import { FormItemLabel, ModalSubTitle, ModalTitle } from '../../../components/text/Titles';
import UploadMultiContainer from '../upload/UploadMultiContainer';

type Props = {
  visible: boolean;
  onClose: () => any;
  onCreate: (training: Training) => any;
  model: Model;
  version: null | ModelVersion;
};

const CreateVersion = (props: Props) => {
  const { visible, onClose, onCreate, model, version } = props;
  const annotationTypes = (model && model.info && model.info.annotationTypes && model.info.annotationTypes) || [];

  const [versionValue, setVersionValue] = useState<string>('');
  const [inferenceList, setInferenceList] = useState<InferenceFile[]>([]);
  const [step, setStep] = useState(0);

  const goToUploadEnabled = !!versionValue;
  const submitEnabled =
    inferenceList &&
    inferenceList.length > 0 &&
    inferenceList.every(inference => ['FINISHED', 'ERROR'].includes(inference.status));

  const onVersionChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setVersionValue(event.currentTarget.value);
  };

  const goToUpload = () => {
    setStep(1);
  };

  const onSubmit = async () => {
    const training = await createTraining(
      model.id,
      inferenceList.map(inference => inference.id),
      versionValue
    );
    onModalClose();
    onCreate(training);
  };

  const onCancel = async () => {
    await Promise.all(inferenceList.map(inference => deleteInference(inference.id)));
    onClose();
  };

  const onModalClose = async () => {
    setVersionValue('');
    setStep(0);

    onClose();
  };

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={<ModalTitle>Train new version</ModalTitle>}
      onCancel={onCancel}
      footer={
        step === 0 ? (
          <div>
            <Button type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Tooltip title={goToUploadEnabled ? '' : 'Enter a version name before continuing.'}>
              <Button style={{ marginLeft: '10px' }} type="primary" onClick={goToUpload} disabled={!goToUploadEnabled}>
                Next
              </Button>
            </Tooltip>
          </div>
        ) : (
          <div>
            <Button type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Tooltip title={submitEnabled ? '' : 'Upload at least 1 document and wait for them to finish analysing.'}>
              <Button type="primary" onClick={onSubmit} style={{ marginLeft: '10px' }} disabled={!submitEnabled}>
                Review
              </Button>
            </Tooltip>
          </div>
        )
      }>
      {step === 0 ? (
        <div>
          <FormItemLabel>This process will upgrade an existing model.</FormItemLabel>
          <ModalSubTitle>Current version</ModalSubTitle>
          <BlockedDiv alteredPadding>
            <Descriptions layout="horizontal" column={1}>
              <Descriptions.Item label="Model name">{model.name}</Descriptions.Item>
              <Descriptions.Item label="Version">{version && version.modelVersionName}</Descriptions.Item>
              <Descriptions.Item label="Labels">
                {annotationTypes.map((label, index) => {
                  return (
                    <Tag
                      key={index}
                      style={{
                        padding: '5px',
                        marginTop: '10px',
                        backgroundColor: '#ffffff',
                        borderColor: '#e8e8e8',
                      }}>
                      <span
                        style={{
                          color: '#6d6e77',
                          margin: '5px',
                          fontSize: '12px',
                        }}>
                        {getLabelValue(label)}
                      </span>
                    </Tag>
                  );
                })}
              </Descriptions.Item>
            </Descriptions>
          </BlockedDiv>
          <ModalSubTitle>New version</ModalSubTitle>
          <Input placeholder="Version" onChange={onVersionChange} style={{ margin: '10px 0px 10px 0px' }} />
        </div>
      ) : (
        <div>
          <FormItemLabel>
            To create an updated model, we need some example documents. We advise you to upload and review a minimum of
            3 documents.
          </FormItemLabel>
          {model.algorithm.multiflow ? (
            <UploadMultiContainer
              modelId={model.id}
              inferenceList={inferenceList}
              onInferenceListUpdated={inferenceList => setInferenceList(inferenceList)}
              flowName={model.info.flowName || ''}
            />
          ) : (
            <UploadContainer
              modelId={model.id}
              inferenceList={inferenceList}
              onInferenceListUpdated={inferenceList => setInferenceList(inferenceList)}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default CreateVersion;
